import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Divider,
  Stack,
  Menu,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import LockResetIcon from '@mui/icons-material/LockReset';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ApartmentIcon from '@mui/icons-material/Apartment';
import config from '../config';

import ExecutiveOverview from './ExecutiveOverview.jsx';
import ChargesAndCollections from './ChargesAndCollections.jsx';
import PerformanceAnalysis from './PerformanceAnalysis.jsx';

const drawerWidth = '16.25rem';
const collapsedDrawerWidth = '5rem';

function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [counts, setCounts] = useState({ locations: 0, providers: 0 });
  const [filters, setFilters] = useState({
    year: '2025',
    month: 'All',
    location: 'All',
    dateType: 'post',
  });
  const [consolidateLocations, setConsolidateLocations] = useState(true);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // Get tenant and user info from localStorage
  const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch counts on mount
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.apiBaseUrl}/api/executive-overview`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.counts) {
            setCounts(data.counts);
          }
        } else if (response.status === 401) {
          // Token expired or invalid, logout
          onLogout();
          navigate('/login');
        }
      } catch (err) {
        console.error('Error fetching counts:', err);
      }
    };
    fetchCounts();
  }, [navigate, onLogout]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleChangePassword = () => {
    handleUserMenuClose();
    navigate('/change-password');
  };

  const handleLogout = () => {
    handleUserMenuClose();
    onLogout();
  };

  const menuItems = [
    {
      text: 'Executive Overview',
      path: '',
      icon: <DashboardIcon />,
      description: 'High-level metrics & KPIs'
    },
    {
      text: 'Charges & Payments',
      path: 'charges-collections',
      icon: <AttachMoneyIcon />,
      description: 'Detailed revenue analysis'
    },
    {
      text: 'Performance Analysis',
      path: 'performance',
      icon: <ShowChartIcon />,
      description: 'Insights & benchmarks'
    },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: sidebarCollapsed ? 1 : 3, py: 2, justifyContent: 'center' }}>
        {!sidebarCollapsed ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: '2.25rem',
                height: '2.25rem',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
              }}
            >
              PLL
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Dashboard
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: '2.25rem',
              height: '2.25rem',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 700,
            }}
          >
            PLL
          </Box>
        )}
      </Toolbar>

      {/* Practice Stats Section */}
      <Box sx={{ px: sidebarCollapsed ? 1.5 : 3, py: 2 }}>
        <Stack direction="column" spacing={1}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarCollapsed ? 'center' : 'space-between',
              bgcolor: '#f8fafc',
              borderRadius: 2,
              px: sidebarCollapsed ? 0.75 : 1.5,
              py: sidebarCollapsed ? 0.75 : 1,
              border: '1px solid #e2e8f0',
              gap: sidebarCollapsed ? 0.5 : 0,
            }}
          >
            {sidebarCollapsed ? (
              <>
                <LocationOnIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                <Typography sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.75rem' }}>
                  {counts.locations}
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocationOnIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                    Locations
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.25,
                    fontWeight: 700,
                    fontSize: '0.813rem',
                  }}
                >
                  {counts.locations}
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarCollapsed ? 'center' : 'space-between',
              bgcolor: '#f8fafc',
              borderRadius: 2,
              px: sidebarCollapsed ? 0.75 : 1.5,
              py: sidebarCollapsed ? 0.75 : 1,
              border: '1px solid #e2e8f0',
              gap: sidebarCollapsed ? 0.5 : 0,
            }}
          >
            {sidebarCollapsed ? (
              <>
                <PersonIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                <Typography sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.75rem' }}>
                  {counts.providers}
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PersonIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                    Providers
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.25,
                    fontWeight: 700,
                    fontSize: '0.813rem',
                  }}
                >
                  {counts.providers}
                </Box>
              </>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Date View Toggle Section */}
      <Box sx={{ px: sidebarCollapsed ? 1.5 : 3, py: 2 }}>
        {!sidebarCollapsed ? (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              bgcolor: '#f1f5f9',
              borderRadius: '1.5rem',
              padding: '0.25rem',
              cursor: 'pointer',
            }}
          >
            {/* Sliding Blue Pill */}
            <Box
              sx={{
                position: 'absolute',
                top: '0.25rem',
                left: filters.dateType === 'post' ? '0.25rem' : 'calc(50%)',
                width: 'calc(50% - 0.25rem)',
                height: 'calc(100% - 0.5rem)',
                bgcolor: '#2563eb',
                borderRadius: '1.25rem',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)',
              }}
            />
            {/* Post Date Label */}
            <Box
              onClick={() => setFilters({ ...filters, dateType: 'post' })}
              sx={{
                flex: 1,
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                transition: 'color 0.3s ease',
                color: filters.dateType === 'post' ? 'white' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              Post Date
            </Box>
            {/* Service Date Label */}
            <Box
              onClick={() => setFilters({ ...filters, dateType: 'service' })}
              sx={{
                flex: 1,
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                transition: 'color 0.3s ease',
                color: filters.dateType === 'service' ? 'white' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              Service Date
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => setFilters({ ...filters, dateType: filters.dateType === 'post' ? 'service' : 'post' })}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              bgcolor: '#f8fafc',
              borderRadius: 2,
              px: 0.75,
              py: 1,
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#f1f5f9',
                borderColor: '#cbd5e1',
                transform: 'scale(1.05)',
              }
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
            <Typography sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.625rem' }}>
              {filters.dateType === 'post' ? 'PD' : 'SD'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Facility View Toggle Section */}
      <Box sx={{ px: sidebarCollapsed ? 1.5 : 3, py: 2 }}>
        {!sidebarCollapsed ? (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              bgcolor: '#f1f5f9',
              borderRadius: '1.5rem',
              padding: '0.25rem',
              cursor: 'pointer',
            }}
          >
            {/* Sliding Blue Pill */}
            <Box
              sx={{
                position: 'absolute',
                top: '0.25rem',
                left: consolidateLocations ? '0.25rem' : 'calc(50%)',
                width: 'calc(50% - 0.25rem)',
                height: 'calc(100% - 0.5rem)',
                bgcolor: '#2563eb',
                borderRadius: '1.25rem',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)',
              }}
            />
            {/* Consolidated Label */}
            <Box
              onClick={() => setConsolidateLocations(true)}
              sx={{
                flex: 1,
                py: 0.5,
                px: 1,
                fontSize: '0.688rem',
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                transition: 'color 0.3s ease',
                color: consolidateLocations ? 'white' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              Consolidated
            </Box>
            {/* Individual Label */}
            <Box
              onClick={() => setConsolidateLocations(false)}
              sx={{
                flex: 1,
                py: 0.5,
                px: 1,
                fontSize: '0.688rem',
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                transition: 'color 0.3s ease',
                color: !consolidateLocations ? 'white' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              Individual
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => setConsolidateLocations(!consolidateLocations)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              bgcolor: '#f8fafc',
              borderRadius: 2,
              px: 0.75,
              py: 1,
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#f1f5f9',
                borderColor: '#cbd5e1',
                transform: 'scale(1.05)',
              }
            }}
          >
            <ApartmentIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
            <Typography sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.625rem' }}>
              {consolidateLocations ? 'C' : 'I'}
            </Typography>
          </Box>
        )}
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: sidebarCollapsed ? 1.5 : 3, py: 3 }}>
        <Stack spacing={2.5} alignItems="center">
          {menuItems.map((item) => {
            const isActive = location.pathname === `/dashboard/${item.path}` ||
                            (location.pathname === '/dashboard' && item.path === '');
            return (
              <Box
                key={item.path}
                onClick={() => navigate(`/dashboard/${item.path}`)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    width: '4rem',
                    height: '4rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    bgcolor: isActive ? '#2563eb' : '#f1f5f9',
                    mb: sidebarCollapsed ? 0 : 1.5,
                    color: isActive ? 'white' : '#64748b',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isActive ? '#1e40af' : '#e2e8f0',
                      transform: 'scale(1.05)',
                    },
                    '& svg': {
                      fontSize: '2rem',
                    }
                  }}
                >
                  {item.icon}
                </Box>
                {!sidebarCollapsed && (
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: isActive ? '#2563eb' : '#1e293b',
                      textAlign: 'center',
                    }}
                  >
                    {item.text}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'white',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#64748b' }}
          >
            <MenuIcon />
          </IconButton>

          {/* PLL Logo - Left */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/PhysicanslifelineLogo.png"
              alt="PhysiciansLine"
              style={{ height: '2.5rem', objectFit: 'contain' }}
            />
          </Box>

          {/* Tenant Logo - Center */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
            {tenant.logoUrl && (
              <img
                src={tenant.logoUrl}
                alt={tenant.name || 'Tenant Logo'}
                style={{ height: '3.125rem', objectFit: 'contain' }}
              />
            )}
          </Box>

          {/* User Menu - Right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#64748b', display: { xs: 'none', sm: 'block' } }}>
              {user.fullName || user.email || 'User'}
            </Typography>
            <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
              <Avatar sx={{ width: '2.25rem', height: '2.25rem', bgcolor: '#2563eb' }}>
                {user.fullName ? user.fullName.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleChangePassword}>
                <ListItemIcon>
                  <LockResetIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s ease',
          '& .MuiDrawer-paper': {
            width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            borderRight: '1px solid #e2e8f0',
            transition: 'width 0.2s ease',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Collapse/Expand Button - Desktop only */}
      <IconButton
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        sx={{
          display: { xs: 'none', sm: 'flex' },
          position: 'fixed',
          left: sidebarCollapsed ? 'calc(5rem - 1rem)' : 'calc(16.25rem - 1rem)',
          top: '5.25rem',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          width: '2rem',
          height: '2rem',
          bgcolor: 'white',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'left 0.2s ease',
          '&:hover': {
            bgcolor: '#f8fafc',
            borderColor: '#cbd5e1',
          },
        }}
      >
        {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: '1.125rem' }} /> : <ChevronLeftIcon sx={{ fontSize: '1.125rem' }} />}
      </IconButton>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth})` },
          mt: 8,
          transition: 'width 0.2s ease',
        }}
      >
        <Routes>
          <Route path="/" element={<ExecutiveOverview filters={filters} consolidateLocations={consolidateLocations} />} />
          <Route path="/charges-collections" element={<ChargesAndCollections filters={filters} consolidateLocations={consolidateLocations} />} />
          <Route path="/performance" element={<PerformanceAnalysis filters={filters} consolidateLocations={consolidateLocations} />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default Dashboard;
